/**
 * InternalBookingDialog - Dialog multi-step para criação de agendamento interno
 * 
 * Permite criar agendamentos internamente com:
 * - Seleção de tipo de evento
 * - Override de vendedor (host)
 * - Seleção/criação de contato
 * - Vinculação a negócio existente, criação automática ou sem negócio
 * - Seleção de data/horário com validação de slots
 * - Dados do convidado pré-preenchidos do contato
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-10 v1.0.0 - Implementação inicial
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarDays, Clock, User, Briefcase, CheckCircle2, ArrowRight, ArrowLeft,
  Loader2, AlertCircle, Check, Settings, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';
import { stripTemplateVariables } from '@/lib/schedulingUtils';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import { PhoneInput } from '@/components/ui/phone-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { SellerSelector } from '@/components/crm/SellerSelector';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { DealSelector } from '@/components/tasks/DealSelector';

import { useSchedulingEventTypes, type SchedulingEventType } from '@/hooks/useSchedulingEventTypes';
import { useAvailableSlots, useBlockedDates, useUnavailableWeekdays } from '@/hooks/usePublicScheduling';
import { useInternalBooking, type InternalBookingInput } from '@/hooks/useInternalBooking';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { formatPhoneForDisplay } from '@/lib/phone-utils';

const logger = createLogger('InternalBookingDialog');

// ============================================================================
// TIPOS
// ============================================================================

interface InternalBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Modo de vinculação do negócio */
type DealMode = 'existing' | 'auto' | 'none';

/** Etapas do wizard */
type Step = 1 | 2 | 3 | 4;

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function InternalBookingDialog({ open, onOpenChange }: InternalBookingDialogProps) {
  const { currentTenant } = useAuth();
  const { eventTypes, isLoading: isLoadingEventTypes } = useSchedulingEventTypes();
  const { activeSellers } = useCrmSellers();
  const { createBooking, isCreating } = useInternalBooking();

  // ---- Estado do wizard ----
  const [step, setStep] = useState<Step>(1);

  // Step 1: Configuração
  const [selectedEventTypeId, setSelectedEventTypeId] = useState<string>('');
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  // Step 2: Negócio
  const [dealMode, setDealMode] = useState<DealMode>('auto');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);

  // Step 3: Data/Horário + Dados do convidado
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestNotes, setGuestNotes] = useState('');
  const [skipSlotValidation, setSkipSlotValidation] = useState(false);

  // ---- Derivados ----
  const activeEventTypes = useMemo(
    () => eventTypes.filter((et) => et.is_active),
    [eventTypes]
  );

  const selectedEventType = useMemo(
    () => activeEventTypes.find((et) => et.id === selectedEventTypeId) || null,
    [activeEventTypes, selectedEventTypeId]
  );

  // v1.8.1: Passa seller_id diretamente; a RPC resolve user_id server-side

  // ---- Hooks de disponibilidade (condicionais ao event type e data) ----
  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const timezone = selectedEventType?.timezone || 'America/Sao_Paulo';

  const { data: availableSlots = [], isLoading: isLoadingSlots } = useAvailableSlots(
    selectedEventTypeId || undefined,
    dateStr,
    timezone
  );

  const { data: blockedDates = [] } = useBlockedDates(
    selectedEventTypeId || undefined
  );

  const { data: unavailableWeekdays = [] } = useUnavailableWeekdays(
    selectedEventTypeId || undefined
  );

  // Função para desabilitar datas no calendário
  const isDateDisabled = useCallback(
    (date: Date) => {
      const dateString = format(date, 'yyyy-MM-dd');
      // Verificar datas bloqueadas
      if (blockedDates.includes(dateString)) return true;
      // Verificar dias da semana indisponíveis (0=domingo ... 6=sábado)
      if (unavailableWeekdays.includes(date.getDay())) return true;
      return false;
    },
    [blockedDates, unavailableWeekdays]
  );

  // ---- Busca dados do contato selecionado para pré-preencher Step 3 ----
  const { data: contactData } = useQuery({
    queryKey: ['contact-data-for-booking', selectedContactId],
    queryFn: async () => {
      if (!selectedContactId) return null;
      const { data, error } = await supabase.rpc('search_contacts_for_selector', {
        p_contact_id: selectedContactId,
      });
      if (error || !data || data.length === 0) return null;
      return data[0] as {
        name: string | null;
        phone: string | null;
        email: string | null;
        customer_name: string | null;
        customer_id: string | null;
      };
    },
    enabled: !!selectedContactId,
  });

  // Pré-preenche campos do convidado quando contato é selecionado
  useEffect(() => {
    if (contactData) {
      // Prioriza dados do cliente vinculado (customer_name) se existir
      const name = (contactData.customer_id && contactData.customer_name)
        ? contactData.customer_name
        : contactData.name || '';
      setGuestName(name);
      setGuestEmail(contactData.email || '');
      setGuestPhone(contactData.phone || '');
      logger.info('Dados do contato pré-preenchidos no formulário', { contactId: selectedContactId });
    }
  }, [contactData, selectedContactId]);

  // ---- Reset ao fechar ----
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedEventTypeId('');
      setSelectedSellerId(null);
      setSelectedContactId(null);
      setDealMode('auto');
      setSelectedDealId(null);
      setSelectedDate(undefined);
      setSelectedSlot(null);
      setGuestName('');
      setGuestEmail('');
      setGuestPhone('');
      setGuestNotes('');
      setSkipSlotValidation(false);
    }
  }, [open]);

  // ---- Validações por step ----
  const canAdvanceStep1 = !!selectedEventTypeId;
  const canAdvanceStep2 = dealMode === 'existing' ? !!selectedDealId : true;
  const canAdvanceStep3 =
    (!!selectedSlot || skipSlotValidation) &&
    guestName.trim() !== '' &&
    guestEmail.trim() !== '';

  // ---- Handlers de navegação ----
  const handleNext = () => {
    if (step < 4) setStep((s) => (s + 1) as Step);
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => (s - 1) as Step);
  };

  // ---- Submissão final ----
  const handleConfirm = async () => {
    if (!selectedEventType || !guestName || !guestEmail) return;

    // Construir startAt: se skipSlotValidation sem slot, usar data selecionada + 09:00
    let startAt = selectedSlot;
    if (!startAt && skipSlotValidation && selectedDate) {
      startAt = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 9, 0).toISOString();
    }
    if (!startAt) return;

    const input: InternalBookingInput = {
      eventTypeId: selectedEventTypeId,
      startAt,
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim(),
      guestPhone: guestPhone.trim() || undefined,
      guestNotes: guestNotes.trim() || undefined,
      timezone,
      dealId: dealMode === 'existing' ? selectedDealId : null,
      hostSellerId: selectedSellerId,
      skipSlotValidation,
    };

    try {
      await createBooking(input);
      toast.success('Agendamento criado com sucesso!');
      onOpenChange(false);
    } catch (error: any) {
      logger.error('Erro ao criar agendamento interno', error);
      toast.error('Erro ao criar agendamento', {
        description: error.message || 'Tente novamente',
      });
    }
  };

  // ============================================================================
  // CONFIGURAÇÃO DOS STEPS DO WIZARD
  // ============================================================================

  const WIZARD_STEPS: { key: Step; label: string; description: string; icon: React.ElementType }[] = [
    { key: 1, label: 'Configuração', description: 'Evento, vendedor e contato', icon: Settings },
    { key: 2, label: 'Negócio', description: 'Vínculo com CRM', icon: Briefcase },
    { key: 3, label: 'Agenda', description: 'Data, horário e convidado', icon: CalendarDays },
    { key: 4, label: 'Confirmação', description: 'Revisar e confirmar', icon: CheckCircle2 },
  ];

  /** Verifica se um step está completo */
  const isStepCompleted = (s: Step): boolean => s < step;

  /** Verifica se um step está desabilitado para clique */
  const isStepDisabled = (s: Step): boolean => {
    if (s <= step) return false;
    if (s === 2 && !canAdvanceStep1) return true;
    if (s === 3 && (!canAdvanceStep1 || !canAdvanceStep2)) return true;
    if (s === 4 && (!canAdvanceStep1 || !canAdvanceStep2 || !canAdvanceStep3)) return true;
    return false;
  };

  /** Navegar para um step clicado na sidebar */
  const handleStepClick = (s: Step) => {
    if (!isStepDisabled(s)) setStep(s);
  };

  // Nome do vendedor selecionado para exibir no resumo da sidebar
  const selectedSellerName = selectedSellerId
    ? activeSellers.find((s) => s.id === selectedSellerId)?.name || null
    : null;

  // Step ativo no array de config
  const currentStepConfig = WIZARD_STEPS.find((s) => s.key === step)!;
  const CurrentStepIcon = currentStepConfig.icon;

  // ============================================================================
  // RENDERIZAÇÃO - Layout Wizard Premium (padrão do sistema)
  // ============================================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90dvh] h-[85dvh] p-0 gap-0 overflow-hidden flex flex-col"
        hideCloseButton
      >
        {/* Layout grid: sidebar premium + conteúdo - min-h-0 garante contenção de altura */}
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">

          {/* ============================================================ */}
          {/* SIDEBAR PREMIUM - Gradiente azul escuro (padrão do sistema) */}
          {/* ============================================================ */}
          <aside
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)',
            }}
          >
            {/* Elementos decorativos de fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            {/* Conteúdo da sidebar */}
            <div className="relative z-10 flex flex-col h-full p-5">

              {/* Header da sidebar */}
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <CalendarDays className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-white">Novo Agendamento</h2>
                <p className="text-sm text-white/60 mt-1">
                  Etapa {step} de {WIZARD_STEPS.length}
                </p>
              </div>

              {/* Lista de Steps */}
              <nav className="flex-1 space-y-1">
                {WIZARD_STEPS.map((ws) => {
                  const completed = isStepCompleted(ws.key);
                  const isActive = ws.key === step;
                  const isPending = !completed && !isActive;
                  const disabled = isStepDisabled(ws.key);
                  const Icon = ws.icon;

                  return (
                    <button
                      key={ws.key}
                      type="button"
                      onClick={() => !disabled && handleStepClick(ws.key)}
                      disabled={disabled}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all duration-200',
                        isActive && 'bg-white/10 border border-white/20',
                        completed && !isActive && 'hover:bg-white/5 cursor-pointer',
                        disabled && 'opacity-50 cursor-not-allowed',
                        !isActive && !disabled && 'hover:bg-white/5',
                      )}
                    >
                      {/* Círculo indicador */}
                      <div
                        className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 border-2',
                          completed && 'bg-green-500 border-green-500',
                          isActive && 'bg-white border-white',
                          isPending && 'bg-transparent border-white/30',
                        )}
                      >
                        {completed ? (
                          <Check className="h-4 w-4 text-white" />
                        ) : (
                          <Icon
                            className={cn(
                              'h-4 w-4',
                              isActive ? 'text-primary' : 'text-white/60',
                            )}
                          />
                        )}
                      </div>

                      {/* Texto do step */}
                      <div className="flex-1 min-w-0">
                        <span
                          className={cn(
                            'block text-sm font-medium truncate',
                            isActive ? 'text-white' : 'text-white/80',
                          )}
                        >
                          {ws.label}
                        </span>
                        <span className="block text-xs text-white/40 truncate">
                          {ws.description}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* Resumo no rodapé da sidebar */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <h3 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">
                  Resumo
                </h3>
                <div className="space-y-2">
                  {selectedEventType && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-white/60" />
                        <span className="text-sm text-white/80">Evento</span>
                      </div>
                      <span className="text-sm font-medium text-white truncate max-w-[120px]">
                        {stripTemplateVariables(selectedEventType.title)}
                      </span>
                    </div>
                  )}
                  {selectedSellerName && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-white/60" />
                        <span className="text-sm text-white/80">Host</span>
                      </div>
                      <span className="text-sm font-medium text-white truncate max-w-[120px]">
                        {selectedSellerName}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-white/60" />
                      <span className="text-sm text-white/80">Negócio</span>
                    </div>
                    <span className="text-sm font-medium text-white">
                      {dealMode === 'auto' ? 'Auto' : dealMode === 'existing' ? 'Existente' : 'Nenhum'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* ============================================================ */}
          {/* ÁREA DE CONTEÚDO (direita) */}
          {/* ============================================================ */}
          <main className="flex flex-col h-full min-h-0 overflow-hidden">

            {/* Header do step atual */}
            <div className="flex-shrink-0 flex items-center gap-3 px-6 py-4 border-b bg-background">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <CurrentStepIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold">{currentStepConfig.label}</h2>
                <p className="text-sm text-muted-foreground">{currentStepConfig.description}</p>
              </div>
              <div className="ml-auto md:hidden text-xs text-muted-foreground">
                {step}/{WIZARD_STEPS.length}
              </div>
            </div>

            {/* Conteúdo scrollável */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-6 space-y-4">

                {/* STEP 1: Configuração do Agendamento */}
                {step === 1 && (
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-1.5">
                        <CalendarDays className="h-3 w-3" />
                        Tipo de Evento *
                      </Label>
                      <Select value={selectedEventTypeId} onValueChange={setSelectedEventTypeId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um tipo de evento..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activeEventTypes.map((et) => (
                            <SelectItem key={et.id} value={et.id}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-3 w-3 rounded-full shrink-0"
                                  style={{ backgroundColor: et.color || 'hsl(var(--primary))' }}
                                />
                                <span>{stripTemplateVariables(et.title)}</span>
                                <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">
                                  {et.duration_minutes}min
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-1.5">
                        <User className="h-3 w-3" />
                        Vendedor / Host (opcional)
                      </Label>
                      <SellerSelector
                        value={selectedSellerId}
                        onSelect={setSelectedSellerId}
                        placeholder="Usar host padrão do evento"
                        allowClear
                      />
                      {/* v1.8.1: Warning removido - a RPC valida server-side */}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs flex items-center gap-1.5">
                        <User className="h-3 w-3" />
                        Contato (opcional)
                      </Label>
                      <ContactSelector
                        value={selectedContactId}
                        onSelect={setSelectedContactId}
                        placeholder="Selecione um contato para pré-preencher dados..."
                      />
                    </div>

                    {selectedEventType && (
                      <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
                        <p className="text-sm font-medium">{stripTemplateVariables(selectedEventType.title)}</p>
                        {selectedEventType.description && (
                          <p className="text-xs text-muted-foreground">{selectedEventType.description}</p>
                        )}
                        <div className="flex gap-3 text-xs text-muted-foreground">
                          <span>⏱ {selectedEventType.duration_minutes} min</span>
                          <span>🌐 {selectedEventType.timezone}</span>
                          {selectedEventType.crm_auto_create_deal && (
                            <span>📋 Auto-cria negócio</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 2: Negócio */}
                {step === 2 && (
                  <div className="space-y-4">
                    <Label className="text-xs flex items-center gap-1.5">
                      <Briefcase className="h-3 w-3" />
                      Vinculação com Negócio
                    </Label>

                    <RadioGroup
                      value={dealMode}
                      onValueChange={(v) => {
                        setDealMode(v as DealMode);
                        if (v !== 'existing') setSelectedDealId(null);
                      }}
                      className="space-y-3"
                    >
                      {selectedEventType?.crm_auto_create_deal && (
                        <div className="flex items-start gap-3 rounded-lg border p-3">
                          <RadioGroupItem value="auto" id="deal-auto" className="mt-0.5" />
                          <div>
                            <label htmlFor="deal-auto" className="text-sm font-medium cursor-pointer">
                              Criar negócio automaticamente
                            </label>
                            <p className="text-xs text-muted-foreground">
                              Usa as configurações CRM do tipo de evento (pipeline, fase, fonte)
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-3 rounded-lg border p-3">
                        <RadioGroupItem value="existing" id="deal-existing" className="mt-0.5" />
                        <div className="flex-1 space-y-2">
                          <label htmlFor="deal-existing" className="text-sm font-medium cursor-pointer">
                            Vincular a negócio existente
                          </label>
                          {dealMode === 'existing' && (
                            <DealSelector
                              value={selectedDealId}
                              onSelect={setSelectedDealId}
                              contactId={selectedContactId}
                              placeholder="Buscar negócio..."
                            />
                          )}
                        </div>
                      </div>

                      <div className="flex items-start gap-3 rounded-lg border p-3">
                        <RadioGroupItem value="none" id="deal-none" className="mt-0.5" />
                        <div>
                          <label htmlFor="deal-none" className="text-sm font-medium cursor-pointer">
                            Sem negócio
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Agendamento standalone, sem vínculo com CRM
                          </p>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {/* STEP 3: Data, Horário e Dados do Convidado */}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs flex items-center gap-1.5">
                          <CalendarDays className="h-3 w-3" />
                          Data
                        </Label>
                        <div className="border rounded-lg">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => {
                              setSelectedDate(date ?? undefined);
                              setSelectedSlot(null);
                            }}
                            disabled={isDateDisabled}
                            locale={ptBR}
                            className="rounded-lg"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs flex items-center gap-1.5">
                          <Clock className="h-3 w-3" />
                          Horários Disponíveis
                        </Label>
                        {!selectedDate ? (
                          <div className="flex items-center justify-center h-32 border rounded-lg text-sm text-muted-foreground">
                            Selecione uma data
                          </div>
                        ) : isLoadingSlots ? (
                          <div className="flex items-center justify-center h-32 border rounded-lg">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : availableSlots.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-32 border rounded-lg text-sm text-muted-foreground gap-2">
                            <span>Sem horários disponíveis</span>
                            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={skipSlotValidation}
                                onChange={(e) => setSkipSlotValidation(e.target.checked)}
                                className="rounded"
                              />
                              Agendar fora do horário
                            </label>
                          </div>
                        ) : (
                          <div className="h-[280px] border rounded-lg p-2 overflow-y-auto overflow-x-hidden scrollbar-thin">
                            <div className="grid grid-cols-2 gap-1.5">
                              {availableSlots.map((slot) => {
                                const time = new Date(slot.slot_start);
                                const timeStr = format(time, 'HH:mm');
                                const isSelected = selectedSlot === slot.slot_start;
                                return (
                                  <Button
                                    key={slot.slot_start}
                                    variant={isSelected ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-8 text-xs"
                                    onClick={() => setSelectedSlot(slot.slot_start)}
                                  >
                                    {timeStr}
                                  </Button>
                                );
                              })}
                            </div>
                            <div className="mt-3 pt-2 border-t">
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer text-muted-foreground">
                                <input
                                  type="checkbox"
                                  checked={skipSlotValidation}
                                  onChange={(e) => setSkipSlotValidation(e.target.checked)}
                                  className="rounded"
                                />
                                Pular validação de disponibilidade
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Nome *</Label>
                        <Input
                          value={guestName}
                          onChange={(e) => setGuestName(e.target.value)}
                          placeholder="Nome do convidado"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Email *</Label>
                        <Input
                          type="email"
                          value={guestEmail}
                          onChange={(e) => setGuestEmail(e.target.value)}
                          placeholder="email@exemplo.com"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Telefone</Label>
                        <PhoneInput
                          value={guestPhone}
                          onChange={(val) => setGuestPhone(val)}
                          placeholder="(00) 00000-0000"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Observações</Label>
                      <Textarea
                        value={guestNotes}
                        onChange={(e) => setGuestNotes(e.target.value)}
                        placeholder="Notas adicionais..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* STEP 4: Confirmação */}
                {step === 4 && (
                  <div className="space-y-4">
                    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                      <h3 className="text-sm font-semibold">Resumo do Agendamento</h3>

                      <div className="flex items-start gap-2">
                        <CalendarDays className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">
                            {selectedEventType ? stripTemplateVariables(selectedEventType.title) : '-'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {selectedEventType?.duration_minutes} minutos
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div>
                          {selectedSlot ? (
                            <p className="text-sm">
                              {format(new Date(selectedSlot), "EEEE, dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
                            </p>
                          ) : selectedDate ? (
                            <p className="text-sm">
                              {format(selectedDate, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                              {skipSlotValidation && (
                                <Badge variant="secondary" className="ml-2 text-[9px]">
                                  Fora do horário
                                </Badge>
                              )}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground">-</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{guestName}</p>
                          <p className="text-xs text-muted-foreground">{guestEmail}</p>
                          {guestPhone && (
                            <p className="text-xs text-muted-foreground">{guestPhone}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <Briefcase className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <p className="text-sm">
                          {dealMode === 'existing' && selectedDealId
                            ? 'Vinculado a negócio existente'
                            : dealMode === 'auto'
                              ? 'Negócio será criado automaticamente'
                              : 'Sem negócio vinculado'}
                        </p>
                      </div>

                      {selectedSellerName && (
                        <div className="flex items-start gap-2">
                          <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                          <p className="text-sm">Host: {selectedSellerName}</p>
                        </div>
                      )}

                      {guestNotes && (
                        <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
                          📝 {guestNotes}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer com navegação */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-t bg-background">
              <Button
                variant="outline"
                size="sm"
                onClick={step === 1 ? () => onOpenChange(false) : handleBack}
                className="gap-1"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {step === 1 ? 'Cancelar' : 'Voltar'}
              </Button>

              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground md:hidden">
                  Passo {step} de {WIZARD_STEPS.length}
                </span>

                {step < 4 ? (
                  <Button
                    size="sm"
                    onClick={handleNext}
                    disabled={
                      (step === 1 && !canAdvanceStep1) ||
                      (step === 2 && !canAdvanceStep2) ||
                      (step === 3 && !canAdvanceStep3)
                    }
                    className="gap-1"
                  >
                    Próximo
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleConfirm}
                    disabled={isCreating}
                    className="gap-1"
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Criando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Confirmar Agendamento
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
